import React from "react";
import "./notification-item.scss";

/** Default color tokens matching Figma Pharmacy Prior Auth (node 4602-42635). */
export const NOTIFICATION_ITEM_DEFAULT_COLORS = {
  /** Default container background. */
  backgroundColor: "#ffffff",
  /** Selected/active container background (Tertiary Blue 13). */
  selectedBackgroundColor: "#f3f8ff",
  /** Left accent border when selected (Tertiary Blue 4). */
  accentColor: "#0056d6",
  /** Title text (Primary Gray 1). */
  titleColor: "#0f0f0f",
  /** Timestamp text (Primary Gray 3). */
  timestampColor: "#2e2e2e",
} as const;

export interface NotificationItemColors {
  backgroundColor?: string;
  selectedBackgroundColor?: string;
  accentColor?: string;
  titleColor?: string;
  timestampColor?: string;
}

export interface NotificationItemProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
  /** Notification title (e.g. "PA Resubmission Request Wrong Insurance BJLCCRY2"). */
  title: string;
  /** Relative or absolute timestamp (e.g. "1 hour ago"). */
  timestamp: string;
  /** When true, truncates the title with an ellipsis. Defaults to true. */
  truncateTitle?: boolean;
  /** When true, shows the selected/active state with accent border and highlight background. */
  selected?: boolean;
  /** Custom color tokens; each overrides the default. */
  colors?: NotificationItemColors;
  /** Additional CSS class name for the root element. */
  className?: string;
}

const defaultColors = NOTIFICATION_ITEM_DEFAULT_COLORS;

export const NotificationItem: React.FC<NotificationItemProps> = ({
  title,
  timestamp,
  truncateTitle = true,
  selected = false,
  colors = {},
  className = "",
  onClick,
  style,
  ...rest
}) => {
  const bg = colors.backgroundColor ?? defaultColors.backgroundColor;
  const selectedBg =
    colors.selectedBackgroundColor ?? defaultColors.selectedBackgroundColor;
  const accent = colors.accentColor ?? defaultColors.accentColor;
  const titleColor = colors.titleColor ?? defaultColors.titleColor;
  const timestampColor = colors.timestampColor ?? defaultColors.timestampColor;

  const cssVarStyle: React.CSSProperties = {
    ["--notification-item-bg" as string]: bg,
    ["--notification-item-selected-bg" as string]: selectedBg,
    ["--notification-item-accent" as string]: accent,
    ["--notification-item-title" as string]: titleColor,
    ["--notification-item-timestamp" as string]: timestampColor,
    ...style,
  };

  const isInteractive = typeof onClick === "function";

  return (
    <div
      role={isInteractive ? "button" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      className={[
        "notification-item",
        selected ? "notification-item--selected" : "",
        isInteractive ? "notification-item--interactive" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      style={cssVarStyle}
      onClick={onClick}
      onKeyDown={
        isInteractive
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                event.currentTarget.click();
              }
            }
          : undefined
      }
      data-name="NotificationItem"
      {...rest}
    >
      <div className="notification-item__content">
        <p
          className={[
            "notification-item__title",
            truncateTitle ? "notification-item__title--truncate" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          title={truncateTitle ? title : undefined}
        >
          {title}
        </p>
        <p className="notification-item__timestamp">{timestamp}</p>
      </div>
    </div>
  );
};

export default NotificationItem;
